<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'AuditCenterDrawer',
})
</script>
<template>
    <a-drawer v-model:open="visible" title="审核任务中心" :width="680" placement="right"
        :body-style="{ padding: '0', background: '#f5f7fa' }">
        <template #extra>
            <div style="display: flex; align-items: center; gap: 8px;">
                <a-tag color="#1677ff"
                    style="font-size: 13px; padding: 1px 8px; border-radius: 4px; border: none; font-weight: 500;">
                    待处理总览：{{ props.total }}
                </a-tag>
                <a-button size="small" type="text" :loading="loading" @click="fetchCount" style="color: #1677ff;">
                    刷新
                </a-button>
            </div>
        </template>

        <div class="pro-container">
            <div class="pro-tabs">
                <div class="pro-tab" :class="{ active: activeTab === 'pending' }" @click="activeTab = 'pending'">
                    仅显示待审核
                </div>
                <div class="pro-tab" :class="{ active: activeTab === 'all' }" @click="activeTab = 'all'">
                    全部任务
                </div>
            </div>

            <a-spin :spinning="loading" tip="加载中...">
                <div class="pro-grid">
                    <div v-for="item in filteredList" :key="item.key" class="pro-card"
                        :class="{ 'is-empty': !countMap[item.key] }" @click="goTo(item)">
                        <div class="card-icon"
                            :style="{ backgroundColor: (item.color || '#1677ff') + '10', color: item.color || '#1677ff' }">
                            <component :is="item.icon" style="font-size: 18px;" />
                        </div>

                        <div class="card-body">
                            <div class="card-title">{{ item.title }}</div>
                        </div>

                        <div class="card-right">
                            <span v-if="countMap[item.key]" class="pro-badge">
                                {{ countMap[item.key] > 99 ? '99+' : countMap[item.key] }}
                            </span>
                            <span v-else class="no-data">0</span>
                        </div>
                    </div>
                </div>

                <div v-if="filteredList.length === 0 && !loading" class="pro-empty">
                    <a-empty description="所有任务均已办结" />
                </div>
            </a-spin>
        </div>
    </a-drawer>
</template>

<script lang="ts" setup name="AuditCenterDrawer">
import { computed, ref, watch, shallowRef } from 'vue'
import { useRouter } from 'vue-router'
import {
    FileTextOutlined,
    DeleteOutlined,
    BankOutlined,
    WalletOutlined,
    DownloadOutlined,
    EditOutlined,
    LockOutlined,
    AccountBookOutlined,
    RollbackOutlined,
    TeamOutlined,
    FileDoneOutlined,
    FolderOpenOutlined,
    ToolOutlined,
    SolutionOutlined,
    FileProtectOutlined
} from '@ant-design/icons-vue'

const props = defineProps<{ open: boolean; total: number }>()
const emit = defineEmits(['update:open', 'refresh', 'update:total'])

const router = useRouter()
const visible = computed({
    get: () => props.open,
    set: (v) => emit('update:open', v),
})

const activeTab = ref<'all' | 'pending'>('pending')
const loading = ref(false)
const countMap = ref<Record<string, number>>({})

const auditList = shallowRef([
    { key: 'profile', title: '资料审核', icon: FileTextOutlined, route: '/audit/profile', color: '#1677ff' },
    { key: 'publicTransfer', title: '对公转账', icon: BankOutlined, route: '/audit/publicTransfer', color: '#1677ff' },
    { key: 'recharge', title: '余额充值', icon: WalletOutlined, route: '/audit/recharge', color: '#1677ff' },
    { key: 'download', title: '资料下载', icon: DownloadOutlined, route: '/audit/download', color: '#1677ff' },
    { key: 'attribute', title: '属性调整', icon: EditOutlined, route: '/audit/attribute', color: '#1677ff' },
    { key: 'channel', title: '渠道备案', icon: TeamOutlined, route: '/audit/channel', color: '#1677ff' },
    { key: 'record', title: '备案审核', icon: FileDoneOutlined, route: '/audit/record', color: '#1677ff' },
    { key: 'archive', title: '档案审核', icon: FolderOpenOutlined, route: '/audit/archive', color: '#1677ff' },
    { key: 'workOrder', title: '工单未办结', icon: ToolOutlined, route: '/audit/workOrder', color: '#1677ff' },
    { key: 'license', title: '执照未归档', icon: FileProtectOutlined, route: '/audit/license', color: '#1677ff' },
    { key: 'expense', title: '报销审核', icon: AccountBookOutlined, route: '/audit/expense', color: '#fa8c16' },
    { key: 'refund', title: '主体退款', icon: RollbackOutlined, route: '/audit/refund', color: '#fa8c16' },
    { key: 'invoice', title: '发票申请', icon: SolutionOutlined, route: '/audit/invoice', color: '#fa8c16' },
    { key: 'customerDel', title: '客户删除', icon: DeleteOutlined, route: '/audit/customerDel', color: '#ff4d4f' },
    { key: 'lock', title: '锁定审核', icon: LockOutlined, route: '/audit/lock', color: '#ff4d4f' },
])

watch(() => props.open, (v) => { if (v) fetchCount() })

const fetchCount = async () => {
    loading.value = true
    try {
        const mockData = {
            profile: 12, customerDel: 5, publicTransfer: 8, download: 20,
            channel: 30, record: 40, workOrder: 15, license: 3, invoice: 2,
            refund: 1, expense: 6, recharge: 0, attribute: 0, archive: 0, lock: 0
        }
        countMap.value = mockData

        const sum = Object.values(mockData).reduce((acc, val) => acc + (Number(val) || 0), 0)
        emit('update:total', sum)

    } catch (e) {
    } finally {
        setTimeout(() => { loading.value = false }, 300)
    }
}

const filteredList = computed(() => {
    if (activeTab.value === 'pending') {
        return auditList.value.filter((i) => countMap.value[i.key] > 0)
    }
    return auditList.value
})

const goTo = (item: any) => {
    router.push(item.route)
    visible.value = false
}
</script>

<style lang="less" scoped>
.pro-container {
    min-height: 100%;
    background-color: #f5f7fa;
}

.pro-tabs {
    display: flex;
    background: #ffffff;
    padding: 8px 12px;
    margin-bottom: 8px;
    border-bottom: 1px solid #e5e6eb;

    .pro-tab {
        flex: 1;
        text-align: center;
        font-size: 13px;
        color: #4e5969;
        padding: 6px 0;
        cursor: pointer;
        border-radius: 4px;
        transition: all 0.2s;

        &.active {
            color: #1677ff;
            background: #e8f3ff;
            font-weight: 600;
        }
    }
}

.pro-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 0 12px 12px 12px;
}

.pro-card {
    display: flex;
    align-items: center;
    padding: 10px;
    background: #ffffff;
    border-radius: 6px;
    border: 1px solid #e5e6eb;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        border-color: #1677ff;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    }

    &.is-empty {
        background: #fafafa;
        border-color: #f0f0f0;

        .card-title {
            color: #a3a3a3;
        }
    }

    .card-icon {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 8px;
        flex-shrink: 0;
    }

    .card-body {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
    }

    .card-title {
        font-size: 13px;
        font-weight: 500;
        color: #1d2129;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .card-right {
        margin-left: 6px;
        flex-shrink: 0;
    }

    .pro-badge {
        display: inline-block;
        background-color: #1677ff;
        color: #fff;
        font-size: 11px;
        font-weight: 500;
        padding: 0 5px;
        height: 16px;
        line-height: 16px;
        border-radius: 8px;
        min-width: 16px;
        text-align: center;
    }

    .no-data {
        color: #c9cdd4;
        font-size: 12px;
    }
}

.pro-empty {
    padding-top: 60px;
    text-align: center;
}
</style>
